import React, { useEffect, useMemo } from 'react';
import {
  FrequencyDisplayStringMap,
  SecurityAsset,
  SecurityAssetSecurityType,
  V1TradeExpirationType,
  V1MarketSide,
} from '@fintronners/react-api/src';
import {
  WebBodyText14,
  WebBodyText20,
  WebCaption10,
} from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import {
  apexSharesShortFormat,
  thousandFormatNumbroCurrency,
} from '@fintronners/react-utils/src/numberUtilsTSX';
import { Button } from '@/components/common/Button';
import { AssetOverview } from '@/components/common/AssetOverview';
import { SheetContent, SheetFooter, SheetTitle } from '@/components/common/Sheet';
import { NationalBestBidOfferDialog } from '@/components/features/trade/NationalBestBidOfferDialog/NationalBestBidOfferDialog';
import { TradeFormData } from './schema';
import { useFormContext } from 'react-hook-form';
import { useTenantBasedStrings } from '@/hooks/config/useTenantBasedStrings';
import { useTranslation } from 'react-i18next';
import { OrderType, QuantityType } from '@fintronners/internal-core/src/trade/constants';
import usePreviewTradeFee from '@fintronners/react-widgets/src/hooks/trades/usePreviewTradeFee';
import { useSidePanel } from '@/context/SidePanelContext';
import { TradeConfirmationSkeleton } from './parts/TradeConfirmationSkeleton';
import { FeatureFlags } from '@fintronners/react-config/src/Config/growthbook';
import useGrowthBookFeature from '@fintronners/react-config/src/hooks/useGrowthBookFeature';
import { usePreviewTradeFeeStore } from '@/stores/trades/preview-trade-fee';
import useTradeApiService from '@/hooks/api/useTradeService';
import { TradeFormSteps } from '@/components/common/SidePanels/TradeSidePanel/constants';
import type { TradeBreakdown } from '@/hooks/trade/useTradeBreakdown';
import { TradeSide } from './schema';

interface TradeSidePanelConfirmationProps {
  side: TradeSide;
  asset: SecurityAsset;
  tradeBreakdown: TradeBreakdown;
  isSubmitting: boolean;
  onBack: () => void;
  onContinue: () => void;
}

const displayStrings = {
  summary: 'Order summary',
  orderType: 'Order type:',
  frequency: 'Frequency',
  dollarAmount: 'Dollar amount:',
  orderDuration: 'Order duration:',
  limitPrice: 'Limit price:',
  stopPrice: 'Stop price:',
  estimatedDollarValue: 'Estimated dollar value',
  durationLabels: {
    [V1TradeExpirationType.Day]: '1 Day',
    [V1TradeExpirationType.GoodTilCancel]: 'Good Till Cancel',
    [V1TradeExpirationType.Unspecified]: 'Unspecified',
  },
  orderTypeLabels: {
    marketBuy: 'Market buy',
    marketSell: 'Market sell',
    stopLoss: 'Stop loss',
    limitBuy: 'Limit buy',
    repeatInvestment: 'Repeat Investment',
  },
  buy: 'Buy',
  sell: 'Sell',
  oneDay: '1 day',
  submit: 'Submit',
  back: 'Back',
  securityText: (brokerDealerName: string, clearingFirmName: string) =>
    `Securities are offered via ${brokerDealerName} in a fractional capacity and custodied by ${clearingFirmName}. Investors should be aware that fractional shares represent partial ownership of a security. Investments may lose value and there is a risk of losing money. Brokerage services are offered through ${brokerDealerName}, a registered broker dealer (member FINRA & SIPC).`,
  repeatInvestmentSecurityText: (brokerDealerName: string, clearingFirmName: string) =>
    `You are submitting a repeat investment, which means you are instructing ${brokerDealerName} to transfer the requested dollar amount from your connected bank account at your requested frequency. Repeat investments will be submitted to ${clearingFirmName} for execution as a Market Buy order with a One Day time in force instruction at market open on the third banking day following the successful processing of the transfer of funds.`,
  repeatInvestmentSecurityTextCont: (brokerDealerName: string, clearingFirmName: string) =>
    `Securities are offered via ${brokerDealerName} in a fractional capacity and custodied by ${clearingFirmName}. Investors should be aware that fractional shares represent partial ownership of a security. Investments may lose value and there is a risk of losing money. Brokerage services are offered through ${brokerDealerName}, a registered broker dealer (member FINRA & SIPC).`,
};

export const TradeSidePanelConfirmation = ({
  side,
  asset,
  tradeBreakdown,
  isSubmitting,
  onContinue,
  onBack,
}: TradeSidePanelConfirmationProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<TradeFormData>();

  const { clearingFirm, brokerDealer } = useTenantBasedStrings();
  const { goTo } = useSidePanel();

  const { orderType, quantityType, limitPrice, interval, stopLoss, duration } = getValues();
  const { shareAmount, feeAmount, grossOrderAmount, netOrderAmount } = tradeBreakdown;

  const goToErrorPanel = () => goTo(TradeFormSteps.ERROR);

  const isFeesAndCommissionsEnabled = useGrowthBookFeature(
    FeatureFlags.isFeesAndCommissionsEnabled,
  );

  const tradeApiService = useTradeApiService();
  const {
    setData,
    setError,
    setLoading,
    data: feesAndCommissions,
    loading: isPreviewTradeFeeLoading,
  } = usePreviewTradeFeeStore();

  const onRequestStart = () => {
    setLoading(true);
  };

  const onRequestEnd = () => {
    setLoading(false);
  };

  const { getTradeFee } = usePreviewTradeFee({
    shouldSkip: !isFeesAndCommissionsEnabled,
    apiService: tradeApiService,
    onError: (_error) => {
      setError(_error);
      goToErrorPanel();
    },
    onSuccess: (_data) => {
      setData(_data);
    },
    onRequestStart,
    onRequestEnd,
  });

  useEffect(() => {
    if (asset.id && grossOrderAmount) {
      getTradeFee({
        amount: grossOrderAmount.toString(),
        assetUid: asset.id,
        side: side === TradeSide.BUY ? V1MarketSide.Buy : V1MarketSide.Sell,
      });
    }
  }, [grossOrderAmount, asset.id, side]);

  const isMutualFund = asset.securityType === SecurityAssetSecurityType.MutualFund;

  const orderTypeLabel = useMemo(() => {
    if (isMutualFund) return displayStrings[orderType === OrderType.MARKET_BUY ? 'buy' : 'sell'];

    return displayStrings.orderTypeLabels[orderType] || '';
  }, [orderType]);

  return (
    <SheetContent>
      <div className="flex flex-col gap-7 mt-4">
        <div>
          {asset && asset.market && (
            <AssetOverview
              basePrice={asset.market.previousDayClose}
              price={asset.market.last}
              logoURI={asset.logoURI}
              symbol={asset.symbol}
              name={asset.name}
            />
          )}
          {!isMutualFund && (
            <NationalBestBidOfferDialog securityId={asset.id} isBuy={side === TradeSide.BUY} />
          )}
        </div>
        <div>
          <SheetTitle>
            <WebBodyText20.Bold className="text-darkestBlue">
              {displayStrings.summary}
            </WebBodyText20.Bold>
          </SheetTitle>
          <div className="flex flex-col gap-4 mt-5">
            <div className="flex justify-between">
              <WebBodyText14.Regular className="text-grey72">
                {displayStrings.orderType}
              </WebBodyText14.Regular>
              <WebBodyText14.Bold>{orderTypeLabel}</WebBodyText14.Bold>
            </div>
            {orderType === OrderType.REPEAT_INVESTMENT ? (
              <>
                {interval && (
                  <div className="flex justify-between">
                    <WebBodyText14.Regular className="text-grey72">
                      {displayStrings.frequency}
                    </WebBodyText14.Regular>
                    <WebBodyText14.Bold>{FrequencyDisplayStringMap[interval]}</WebBodyText14.Bold>
                  </div>
                )}
                {isFeesAndCommissionsEnabled && isPreviewTradeFeeLoading ? (
                  <TradeConfirmationSkeleton numberOfLines={3} />
                ) : (
                  <>
                    {feesAndCommissions && (
                      <>
                        <div className="flex justify-between">
                          <WebBodyText14.Regular className="text-grey72">
                            {t('miscScreens.tradeDisplay.tradeOrderEstimatedOrderAmount')}
                          </WebBodyText14.Regular>
                          <WebBodyText14.Bold>
                            {thousandFormatNumbroCurrency(grossOrderAmount)}
                          </WebBodyText14.Bold>
                        </div>
                        <div className="flex justify-between">
                          <WebBodyText14.Regular className="text-grey72">
                            {isMutualFund
                              ? `${t('common.transactionFee')}:`
                              : t('miscScreens.tradeDisplay.tradeOrderTradeFee')}
                          </WebBodyText14.Regular>
                          <WebBodyText14.Bold>
                            {thousandFormatNumbroCurrency(feeAmount)}
                          </WebBodyText14.Bold>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <WebBodyText14.Regular className="text-grey72">
                        {displayStrings.dollarAmount}
                      </WebBodyText14.Regular>
                      <WebBodyText14.Bold>
                        {thousandFormatNumbroCurrency(netOrderAmount)}
                      </WebBodyText14.Bold>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className="flex justify-between">
                  <WebBodyText14.Regular className="text-grey72">
                    {displayStrings.orderDuration}
                  </WebBodyText14.Regular>
                  <WebBodyText14.Bold>
                    {isMutualFund ? displayStrings.oneDay : displayStrings.durationLabels[duration]}
                  </WebBodyText14.Bold>
                </div>
                {orderType === OrderType.LIMIT_BUY && (
                  <div className="flex justify-between">
                    <WebBodyText14.Regular className="text-grey72">
                      {displayStrings.limitPrice}
                    </WebBodyText14.Regular>
                    <WebBodyText14.Bold>
                      {thousandFormatNumbroCurrency(limitPrice)}
                    </WebBodyText14.Bold>
                  </div>
                )}
                {orderType === OrderType.STOP_LOSS && (
                  <div className="flex justify-between">
                    <WebBodyText14.Regular className="text-grey72">
                      {displayStrings.stopPrice}
                    </WebBodyText14.Regular>
                    <WebBodyText14.Bold>
                      {thousandFormatNumbroCurrency(stopLoss)}
                    </WebBodyText14.Bold>
                  </div>
                )}
                {!isMutualFund && (
                  <div className="flex justify-between">
                    <WebBodyText14.Regular className="text-grey72">
                      {quantityType === QuantityType.SHARES
                        ? t('common.shareAmount', { context: 'dataList' })
                        : t('common.estimatedShareAmount', { context: 'dataList' })}
                    </WebBodyText14.Regular>
                    <WebBodyText14.Bold>{apexSharesShortFormat(shareAmount)}</WebBodyText14.Bold>
                  </div>
                )}
                {(!isMutualFund || side === TradeSide.BUY) && (
                  <div className="flex justify-between">
                    <WebBodyText14.Regular className="text-grey72">
                      {quantityType === QuantityType.DOLLARS
                        ? t('common.dollarAmount', { context: 'dataList' })
                        : t('common.estimatedDollarAmount', { context: 'dataList' })}
                    </WebBodyText14.Regular>
                    <WebBodyText14.Bold>
                      {thousandFormatNumbroCurrency(grossOrderAmount)}
                    </WebBodyText14.Bold>
                  </div>
                )}
                {isFeesAndCommissionsEnabled && (
                  <>
                    {isPreviewTradeFeeLoading ? (
                      <TradeConfirmationSkeleton numberOfLines={1} />
                    ) : (
                      <div className="flex justify-between">
                        <WebBodyText14.Regular className="text-grey72">
                          {isMutualFund
                            ? t('common.transactionFee', { context: 'dataList' })
                            : t('miscScreens.tradeDisplay.tradeOrderTradeFee')}
                        </WebBodyText14.Regular>
                        <WebBodyText14.Bold>
                          {feeAmount !== 0
                            ? thousandFormatNumbroCurrency(feeAmount)
                            : t('miscScreens.tradeDisplay.tradeOrderTransactionFeeNoFee')}
                        </WebBodyText14.Bold>
                      </div>
                    )}
                  </>
                )}
                <hr />
                {isPreviewTradeFeeLoading ? (
                  <TradeConfirmationSkeleton numberOfLines={1} />
                ) : (
                  <div className="flex justify-between">
                    <WebBodyText14.Bold>
                      {t('common.estimatedOrderTotal', { context: 'dataList' })}
                    </WebBodyText14.Bold>
                    <WebBodyText14.Bold>
                      {thousandFormatNumbroCurrency(netOrderAmount)}
                    </WebBodyText14.Bold>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex mt-8 text-center">
            {orderType === OrderType.REPEAT_INVESTMENT ? (
              <WebCaption10.Regular className="text-grey55">
                {displayStrings.repeatInvestmentSecurityText(brokerDealer.name, clearingFirm.name)}
                <hr className="border-none" />{' '}
                {displayStrings.repeatInvestmentSecurityTextCont(
                  brokerDealer.name,
                  clearingFirm.name,
                )}
              </WebCaption10.Regular>
            ) : (
              <WebCaption10.Regular className="text-grey55">
                {displayStrings.securityText(brokerDealer.name, clearingFirm.name)}
                {isMutualFund && (
                  <>
                    <hr className="border-none mt-3" />
                    <WebCaption10.Regular className="text-grey55">
                      {t('miscScreens.tradeDisplay.tradeScreenMutualFundOrderTimeDisclosure')}
                    </WebCaption10.Regular>
                  </>
                )}
              </WebCaption10.Regular>
            )}
          </div>
        </div>
      </div>
      <SheetFooter className="my-5 text-center">
        <div className="w-full flex flex-col gap-2">
          <Button onClick={onContinue} disabled={isSubmitting || isPreviewTradeFeeLoading}>
            {displayStrings.submit}
          </Button>
          <Button variant="secondary" onClick={onBack}>
            {displayStrings.back}
          </Button>
        </div>
      </SheetFooter>
    </SheetContent>
  );
};
